import { ApiIncome, Income } from 'src/cashflows/hooks/useIncomes';
import { Expenditure } from 'src/cashflows/hooks/useExpenditures';
import { LifeEvent } from 'src/cashflows/hooks/useLifeEvents';
import { calculateAnnualAmount, calculateMonthlyAmount } from 'src/cashflows/utils';
import { FamilyMember } from './useFamilyMembers';

export type FamilyIncome = Income & { memberName: string };
export type FamilyExpenditure = Expenditure & { memberName: string };
export type FamilyLifeEvent = LifeEvent & { memberName: string };

export type ApiExpenditure = Omit<Expenditure, 'amountAnnually' | 'amountMonthly'>;

// Each perMember* array is aligned by index with `members`; an entry is
// undefined while that member's data is still loading or failed to load.
export function aggregateFamilyIncomes(
  members: FamilyMember[],
  perMemberIncomes: (ApiIncome[] | undefined)[],
): FamilyIncome[] {
  return members.flatMap((member, index) => {
    const data = perMemberIncomes[index];
    if (!data) return [];
    return data.map(income => ({
      ...income,
      amountPreTaxAnnually: calculateAnnualAmount(income.amountPreTax, income.frequency),
      amountPostTaxAnnually: calculateAnnualAmount(income.amountPostTax, income.frequency),
      amountPreTaxMonthly: calculateMonthlyAmount(income.amountPreTax, income.frequency),
      amountPostTaxMonthly: calculateMonthlyAmount(income.amountPostTax, income.frequency),
      memberName: member.client.firstName,
    }));
  });
}

export function aggregateFamilyExpenditures(
  members: FamilyMember[],
  perMemberExpenditures: (ApiExpenditure[] | undefined)[],
): FamilyExpenditure[] {
  return members.flatMap((member, index) => {
    const data = perMemberExpenditures[index];
    if (!data) return [];
    return data.map(expenditure => ({
      ...expenditure,
      amountAnnually: calculateAnnualAmount(expenditure.amount, expenditure.frequency),
      amountMonthly: calculateMonthlyAmount(expenditure.amount, expenditure.frequency),
      memberName: member.client.firstName,
    }));
  });
}

export function aggregateFamilyLifeEvents(
  members: FamilyMember[],
  perMemberLifeEvents: (LifeEvent[] | undefined)[],
): FamilyLifeEvent[] {
  return members.flatMap((member, index) => {
    const data = perMemberLifeEvents[index];
    if (!data) return [];
    return data.map(lifeEvent => ({ ...lifeEvent, memberName: member.client.firstName }));
  });
}
